import { useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import Overlay from 'lib/Overlay';
import ButtonGroup from 'lib/ButtonGroup';
import { Height, Padding, Theme, Width } from 'lib/ButtonGroup/ButtonGroup.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { FeatureFlags } from 'components/common/types/Features.types';
import { Store } from 'components/common/types/Store.types';
import { isDirectSalesCampaignCloned } from 'components/pages/Planner/hooks/utils/isDirectSalesCampaignCloned';
import { DirectSalesLine } from 'components/common/types/DirectSalesCampaign.types';
import { getBackupLines } from 'store/dealManagement/selectors/getBackupLines';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { getCurrentLineBookingStatusCode } from 'store/dealManagement/selectors/getCurrentLineBookingStatusCode';
import { getCurrentLineId } from 'store/dealManagement/selectors/getCurrentLineId';
import { AllocationReportModalProps } from './AllocationReportModal.types';
import AllocationReport from '../AllocationReport';
import { AllocationModalReportType, ReportType, SelectedLine } from '../AllocationReport.types';
import { FramesReport } from '../FramesReport/FramesReport';

const AllocationReportModal: React.FC<AllocationReportModalProps> = ({
  onClose,
  defaultReportType,
  initialReportType,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);

  const dealId = useSelector(getDealId);
  const currentLineId = useSelector(getCurrentLineId);
  const currentLineName = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.name);
  const currentLineStatus = useSelector(getCurrentLineBookingStatusCode);
  const proposalCampaignId = useSelector((state: Store) => state.dealManagement.temporaryDealId);
  const backupLines = useSelector(getBackupLines, shallowEqual);

  const hasFramesReportEnabled = useHasFeatureAccess(FeatureFlags.FRAMES_REPORT) && isDirectSalesCampaignType;

  const [selectedReport, setSelectedReport] = useState<AllocationModalReportType>(initialReportType);

  const toggleReport = (): void => {
    setSelectedReport((prev) => (prev === 'allocation' ? 'frames' : 'allocation'));
  };

  const defaultCurrentLine: SelectedLine = {
    lineDefaultReportType:
      isDirectSalesCampaignType && isDirectSalesCampaignCloned(dealId) ? ReportType.SAVED : ReportType.NOT_SAVED,
    lineId: currentLineId,
    lineName: currentLineName,
    lineStatus: currentLineStatus,
  };

  const dealLines: SelectedLine[] = useMemo(() => {
    if (!backupLines.length) {
      return [defaultCurrentLine];
    }

    const lines: SelectedLine[] = backupLines.map(({ name, lineId, bookingStatusCode, state }: DirectSalesLine) => {
      let lineDefaultReportType;
      let lineStatus;

      if (isDirectSalesCampaignType) {
        lineDefaultReportType = isDirectSalesCampaignCloned(dealId) ? ReportType.NOT_SAVED : ReportType.SAVED;
        lineStatus = state?.status;
      } else {
        lineDefaultReportType =
          lineId === currentLineId && proposalCampaignId && defaultReportType === ReportType.NOT_SAVED
            ? ReportType.NOT_SAVED
            : ReportType.SAVED;
        lineStatus = bookingStatusCode;
      }

      return {
        lineDefaultReportType,
        lineId,
        lineName: name,
        lineStatus,
      };
    });

    if (!currentLineId) {
      lines.push(defaultCurrentLine);
    }

    return lines;
  }, [backupLines, currentLineId, proposalCampaignId]);

  const defaultSelectedLine: SelectedLine =
    dealLines.find(({ lineId }) => lineId === currentLineId) || defaultCurrentLine;

  const changeReportSwitch = (
    <div>
      <ButtonGroup
        options={[
          { text: 'Allocation', value: 'allocation' },
          { text: 'Frames', value: 'frames' },
        ]}
        selectedValue={selectedReport}
        onChange={toggleReport}
        theme={Theme.LIGHT}
        padding={Padding.TIGHT}
        width={Width.MIN}
        height={Height.MEDIUM}
        dataTestId="report-type-switch"
      />
    </div>
  );

  return (
    <Overlay
      header={
        <header className="w-full flex justify-between items-center pr-6">
          <h3 className="sub-header-lg">Allocation report</h3>
        </header>
      }
      onClose={onClose}
      isOpen
      childrenExtraStyles="flex-1 !bg-white min-h-0 overflow-auto"
    >
      {selectedReport === 'allocation' && (
        <AllocationReport
          hasFramesReportEnabled={hasFramesReportEnabled}
          dealLines={dealLines}
          defaultSelectedLine={defaultSelectedLine}
          changeReportSwitch={changeReportSwitch}
          isDirectSales={isDirectSalesCampaignType}
        />
      )}
      {selectedReport === 'frames' && (
        <FramesReport
          dealLines={dealLines}
          defaultSelectedLine={defaultSelectedLine}
          changeReportSwitch={changeReportSwitch}
        />
      )}
    </Overlay>
  );
};
export default AllocationReportModal;
